<template>
  <v-container fluid class="my-clinics-page ds-page">
    <!-- Cabeçalho da página -->
    <v-row class="justify-center mt-2">
      <v-col cols="12" class="text-center">
        <div class="page-section-header justify-center mb-3">
          <v-icon small color="primary" class="mr-2">mdi-domain</v-icon>
          <span class="page-section-title">Suas clínicas</span>
        </div>
        <h1 class="page-title mb-2">
          <v-icon size="28" color="primary" class="mr-2">mdi-hospital-building</v-icon>
          Minhas Clínicas
        </h1>
        <p class="page-subtitle">
          Gerencie as clínicas em que você está associado
        </p>
      </v-col>
    </v-row>

    <!-- Barra de pesquisa e botão adicionar -->
    <v-row class="d-flex justify-center align-center mb-5">
      <v-col cols="12">
        <v-row class="d-flex justify-center align-center" no-gutters>
          <!-- Campo de Pesquisa -->
          <v-col cols="5" class="d-flex align-center" data-tour="clinics-search">
            <v-text-field
              v-model="searchQuery"
              label="Pesquisar clínica"
              prepend-inner-icon="mdi-magnify"
              clearable
              outlined
              color="primary"
              class="mr-2"
              hide-details
              @input="filterClinics"
            />
          </v-col>

          <!-- Botão Adicionar -->
          <!-- salvia-817: adicionar clínica é ação de médico. Para os demais o
               botão era um caminho sem saída, e reforçava a impressão de que dava
               para resolver sozinho. -->
          <v-col v-if="isDoctor" cols="auto" class="d-flex align-center">
            <v-btn
              color="primary"
              rounded
              depressed
              size="large"
              data-tour="clinics-add"
              @click="openAddClinicDialog"
            >
              <v-icon left>mdi-plus</v-icon>
              Adicionar Clínica
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Loading State -->
    <v-row v-if="loading" class="justify-center align-center my-10">
      <v-col cols="12" class="text-center">
        <v-progress-circular
          indeterminate
          color="primary"
          size="70"
        ></v-progress-circular>
        <p class="mt-3 loading-text">Carregando clínicas...</p>
      </v-col>
    </v-row>

    <!-- Empty State -->
    <v-row
      v-else-if="!filteredClinics.length && !searchQuery"
      class="justify-center"
    >
      <!-- salvia-817: o estado vazio precisa ser DIFERENTE por papel.
           O texto antigo mandava "associar-se a uma clínica existente ou adicionar
           uma nova" e oferecia o botão de adicionar — instrução que só serve para o
           médico. Quem não é médico NÃO resolve isso sozinho: o vínculo é feito
           pelo médico, por convite (POST /clinics/invite-user/{id}), e o backend só
           aceita convite de quem já pertence àquela clínica.
           No teste de 27/08 o instinto foi procurar a clínica na própria tela — e a
           busca daqui nunca acha nada, porque "Minhas Clínicas" lista os vínculos
           existentes, não um catálogo. -->
      <v-col cols="12" md="8" lg="6">
        <v-card class="empty-state-card pa-8 text-center">
          <v-icon size="72" color="#C2DDD3" class="mb-4">
            mdi-hospital-building-outline
          </v-icon>

          <template v-if="isDoctor">
            <h3 class="empty-state-title mb-3">
              Você ainda não está associado a nenhuma clínica
            </h3>
            <p class="empty-state-subtitle mb-6">
              Para começar a atender pacientes em clínicas, você precisa se
              associar a uma clínica existente ou adicionar uma nova.
            </p>
            <v-btn
              color="primary"
              rounded
              size="large"
              depressed
              class="empty-state-btn"
              @click="openAddClinicDialog"
            >
              <v-icon left>mdi-plus</v-icon>
              Adicionar Primeira Clínica
            </v-btn>
          </template>

          <template v-else>
            <h3 class="empty-state-title mb-3">
              Você ainda não faz parte de nenhuma clínica
            </h3>
            <p class="empty-state-subtitle mb-2">
              O acesso a uma clínica é liberado pelo médico responsável, que envia
              um convite para o seu e-mail.
            </p>
            <p class="empty-state-subtitle mb-0">
              Peça a ele para convidar você. Assim que aceitar o convite, a clínica
              aparece aqui.
            </p>
          </template>
        </v-card>
      </v-col>
    </v-row>

    <!-- No search results -->
    <v-row
      v-else-if="!filteredClinics.length && searchQuery"
      class="justify-center"
    >
      <v-col cols="12" md="6" class="text-center">
        <v-icon size="60" color="#C2DDD3" class="mb-3">
          mdi-magnify
        </v-icon>
        <h4 class="empty-state-title mb-2">Nenhuma clínica encontrada</h4>
        <p class="empty-state-subtitle">
          Não encontramos clínicas com o termo "{{ searchQuery }}".
        </p>
      </v-col>
    </v-row>

    <!-- Clinics Grid — centrado como o cabeçalho acima; antes um card sozinho
         ficava colado na borda esquerda, desalinhado do título. -->
    <v-row v-else justify="center">
      <v-col
        v-for="(clinic, ci) in filteredClinics"
        :key="clinic.id"
        cols="12"
        sm="6"
        md="4"
        lg="3"
      >
        <v-card
          elevation="3"
          hover
          class="clinic-card h-100 d-flex flex-column"
          :data-tour="ci === 0 ? 'clinics-card' : null"
          :ripple="false"
        >
          <!-- Header with clinic name -->
          <v-card-title class="clinic-header text-white pa-4">
            <v-icon color="white" size="20" class="mr-2 flex-shrink-0">
              mdi-hospital-building
            </v-icon>
            <span class="clinic-title-text">{{ clinic.name }}</span>

            <!-- Menu de opções (botão de 3 pontos) -->
            <v-menu offset-y bottom left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
                  color="white"
                  class="flex-shrink-0 ml-1"
                  :data-tour="ci === 0 ? 'clinics-menu' : null"
                >
                  <v-icon size="20" color="white">mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <!-- Opções no menu -->
                <v-list-item @click="editClinic(clinic.id)">
                  <v-list-item-title>Editar</v-list-item-title>
                </v-list-item>
                <v-list-item
                  :disabled="leavingClinicId === clinic.id"
                  @click="leaveClinic(clinic)"
                >
                  <v-list-item-title class="text-error">
                    Sair da Clínica
                  </v-list-item-title>
                </v-list-item>

                <!-- Menu de ações (ver assistentes, médicos, pacientes) -->
                <v-divider></v-divider>
                <v-list-item @click="viewAssistants(clinic.id)">
                  <v-list-item-icon>
                    <v-icon>mdi-account-tie</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Assistentes</v-list-item-title>
                </v-list-item>
                <v-list-item @click="viewDoctors(clinic.id)">
                  <v-list-item-icon>
                    <v-icon>mdi-doctor</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Profissionais</v-list-item-title>
                </v-list-item>
                <v-list-item @click="viewPatients(clinic.id)">
                  <v-list-item-icon>
                    <v-icon>mdi-account-group</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Pacientes</v-list-item-title>
                </v-list-item>

                <!-- Configuração de agenda (apenas médico) -->
                <template v-if="isDoctor">
                  <v-divider></v-divider>
                  <v-list-item @click="openAgendaConfig(clinic)">
                    <v-list-item-icon>
                      <v-icon color="primary">mdi-calendar-clock</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Configurar Agenda</v-list-item-title>
                  </v-list-item>
                </template>
              </v-list>
            </v-menu>
          </v-card-title>

          <!-- Main content -->
          <v-card-text
            class="flex-grow-1 pa-4"
            :data-tour="ci === 0 ? 'clinics-info' : null"
          >
            <!-- CNPJ -->
            <div class="info-row">
              <div class="info-key">
                <v-icon size="16" color="primary" class="mr-2"
                  >mdi-card-account-details-outline</v-icon
                >
                <span class="info-label">CNPJ</span>
              </div>
              <div class="info-val">{{ formatCNPJ(clinic.cnpj) || "—" }}</div>
            </div>

            <v-divider class="info-divider" />

            <!-- Endereço -->
            <div class="info-row">
              <div class="info-key">
                <v-icon size="16" color="primary" class="mr-2"
                  >mdi-map-marker-outline</v-icon
                >
                <span class="info-label">Endereço</span>
              </div>
              <div class="info-val">
                <div>
                  {{ clinic.street
                  }}<template v-if="clinic.number">, {{ clinic.number }}</template>
                </div>
                <div class="info-val-sub" v-if="clinic.neighborhood">
                  {{ clinic.neighborhood }}
                </div>
                <div class="info-val-sub">
                  {{ clinic.city
                  }}<template v-if="clinic.state"> - {{ clinic.state }}</template>
                </div>
                <div class="info-val-sub" v-if="clinic.cep">
                  CEP {{ formatCEP(clinic.cep) }}
                </div>
              </div>
            </div>

            <v-divider class="info-divider" />

            <!-- Contatos -->
            <div class="info-row">
              <div class="info-key">
                <v-icon size="16" color="primary" class="mr-2"
                  >mdi-phone-outline</v-icon
                >
                <span class="info-label">Contatos</span>
              </div>
              <div class="info-val">
                <template v-if="clinic.phones && clinic.phones.length">
                  <div
                    v-for="(phone, index) in clinic.phones"
                    :key="index"
                    class="info-phone-line"
                  >
                    <v-chip
                      v-if="phone.is_primary"
                      x-small
                      color="success"
                      text-color="white"
                      class="mr-2 flex-shrink-0"
                    >
                      Principal
                    </v-chip>
                    <!-- Número nunca é cortado (dado de contato): fica inteiro
                         numa linha; se faltar espaço, o selo "Principal" quebra. -->
                    <span class="text-no-wrap">{{
                      formatPhone(phone.number)
                    }}</span>
                    <v-icon
                      size="14"
                      class="ml-1 flex-shrink-0"
                      :color="phone.is_primary ? 'success' : 'grey'"
                    >
                      {{ getPhoneIcon(phone.type) }}
                    </v-icon>
                  </div>
                </template>
                <span class="info-val-sub" v-else>
                  Nenhum telefone cadastrado
                </span>
              </div>
            </div>
          </v-card-text>

          <v-card-actions
            class="pa-5 pt-0"
            :data-tour="ci === 0 ? 'clinics-google' : null"
          >
            <div class="w-100 text-center">
              <v-btn
                v-if="!googleAuthStatus[clinic.id]"
                color="red darken-1"
                dark
                @click="redirectToGoogle(clinic.id)"
                prepend-icon="mdi-google"
                class="font-weight-medium"
              >
                Vincular Conta Google
              </v-btn>

              <div v-else>
                <v-chip
                  small
                  color="green lighten-2"
                  text-color="green darken-3"
                >
                  <v-icon left small>mdi-google</v-icon>
                  Conta Google vinculada
                </v-chip>
                <div
                  v-if="clinic.google_account_email"
                  class="text-caption grey--text text--darken-1 mt-1 text-truncate"
                  :title="clinic.google_account_email"
                >
                  {{ clinic.google_account_email }}
                </div>
              </div>
            </div>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog para adicionar clínica -->
    <v-dialog
      v-model="addClinicDialog"
      max-width="600px"
      transition="dialog-bottom-transition"
      scrollable
    >
      <v-card class="v-dialog-card-border elevation-10">
        <v-card-title class="pa-6 d-flex align-center">
          <v-icon color="primary" class="mr-3">mdi-plus</v-icon>
          <span class="text-h6 font-weight-medium text-primary">
            Adicionar Nova Clínica
          </span>
          <v-spacer />
          <v-btn icon class="close-btn" @click="addClinicDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-hospital-building</v-icon>
                <span>Como deseja continuar?</span>
              </div>
            </div>

            <p class="text-body-2 mb-4">
              Você pode se associar a uma clínica existente ou criar uma nova
              clínica.
            </p>

            <v-row dense>
              <v-col cols="12" sm="6">
                <v-card
                  class="pa-4 text-center hover:scale-105 transition-transform cursor-pointer"
                  outlined
                  color="primary"
                  rounded="xl"
                  @click="joinExistingClinic"
                >
                  <v-icon size="36" class="mb-2 text-primary"
                    >mdi-hospital-building</v-icon
                  >
                  <div class="text-subtitle-1 font-weight-medium">
                    Associar-se
                  </div>
                  <div class="text-subtitle-2 text--secondary">
                    a Clínica Existente
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12" sm="6">
                <v-card
                  class="pa-4 text-center hover:scale-105 transition-transform cursor-pointer"
                  outlined
                  color="success"
                  rounded="xl"
                  @click="createNewClinic"
                >
                  <v-icon size="36" class="mb-2 text-success"
                    >mdi-plus-circle</v-icon
                  >
                  <div class="text-subtitle-1 font-weight-medium">Criar Nova</div>
                  <div class="text-subtitle-2 text--secondary">Clínica</div>
                </v-card>
              </v-col>
            </v-row>
          </section>
        </v-card-text>

        <v-card-actions class="ds-dialog-actions">
          <v-btn outlined color="primary" @click="addClinicDialog = false">
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <CheckMyClinicPatientsDialog
      v-model="patientsDialog"
      :clinicId="selectedClinicId"
    />

    <CheckMyClinicAssistantsDialog
      v-model="assistantsDialog"
      :clinicId="selectedClinicId"
    />

    <CheckMyClinicDoctorsDialog
      v-model="doctorsDialog"
      :clinicId="selectedClinicId"
    />

    <ClinicRegisterAndEditDialog
      v-model="clinicDialog"
      :mode="clinicDialogMode"
      :clinicId="clinicDialogClinicId"
      @saved="loadClinics"
    />

    <AgendaConfigDialog
      v-model="agendaDialog"
      :clinicId="agendaClinicId"
      :clinicName="agendaClinicName"
    />
  </v-container>
</template>

<script>
import http from "@/services/http";
import { getMyClinics, leaveClinic } from "@/services/clinicService.js";
import { useSnackbar } from "@/composables/useSnackbar";
import CheckMyClinicPatientsDialog from "@/features/clinics/CheckMyClinicPatientsDialog.vue";
import CheckMyClinicAssistantsDialog from "@/features/clinics/CheckMyClinicAssistantsDialog.vue";
import CheckMyClinicDoctorsDialog from "@/features/clinics/CheckMyClinicDoctorsDialog.vue";
import ClinicRegisterAndEditDialog from "@/features/clinics/ClinicRegisterAndEditDialog.vue";
import AgendaConfigDialog from "@/features/calendar/components/AgendaConfigDialog.vue";
import tourMixin from "@/features/tour/tourMixin";

export default {
  name: "MyClinicsPage",

  mixins: [tourMixin],

  components: {
    CheckMyClinicAssistantsDialog,
    CheckMyClinicDoctorsDialog,
    CheckMyClinicPatientsDialog,
    ClinicRegisterAndEditDialog,
    AgendaConfigDialog,
  },

  setup() {
    const { success, error, erroDeRequisicao } = useSnackbar();
    return { success, error, erroDeRequisicao };
  },

  data() {
    return {
      loading: false,
      leavingClinicId: null,
      searchQuery: "",
      addClinicDialog: false,
      filteredClinics: [],
      clinics: [],
      googleAuthStatus: {},
      patientsDialog: false,
      assistantsDialog: false,
      doctorsDialog: false,
      clinicDialog: false,
      clinicDialogMode: "create", // ou "edit"
      clinicDialogClinicId: null,
      selectedClinicId: null,
      agendaDialog: false,
      agendaClinicId: null,
      agendaClinicName: "",
    };
  },

  computed: {
    isDoctor() {
      const t = this.$store.getters["auth/userType"];
      return t === "doctor_solo" || t === "doctor_clinic";
    },
    tourId() {
      return "clinics";
    },
  },

  mounted() {
    this.initializePage();
  },

  methods: {
    // Só auto-inicia o tour depois de carregar as clínicas (âncoras dos cards).
    tourReady() {
      return !this.loading;
    },
    tourSteps() {
      // Estado de vínculo Google do 1º card (adapta o texto do passo).
      const firstClinic = this.filteredClinics[0];
      const firstLinked = firstClinic
        ? !!this.googleAuthStatus[firstClinic.id]
        : false;

      const googleDescription = firstLinked
        ? "Esta clínica já está com uma Conta Google vinculada (o selo verde “Conta Google vinculada” confirma isso). É essa conta que recebe e cria os eventos da agenda. Para trocar a conta, basta vincular novamente."
        : "Esta clínica ainda NÃO tem uma Conta Google vinculada. Clique em “Vincular Conta Google” e conclua o login: sem esse vínculo, a clínica não aparece disponível ao marcar atendimentos e a agenda dela não funciona. É o primeiro passo antes de agendar.";

      const steps = [
        {
          // Boas-vindas (sem âncora → card centralizado).
          title: "Minhas Clínicas 🏥",
          description:
            "Aqui você gerencia todas as clínicas em que atende: dados, equipe, vínculo com o Google e a configuração de agenda de cada uma. Vou mostrar o essencial — reabra este tour quando quiser pelo botão “?”.",
        },
        {
          anchor: "clinics-search",
          title: "Encontre uma clínica",
          description:
            "Filtre suas clínicas pelo nome conforme digita. Fica prático quando você atende em várias e quer ir direto a uma delas.",
        },
        {
          anchor: "clinics-add",
          title: "Adicione uma clínica",
          description:
            "Associe-se a uma clínica já existente (pelo CNPJ) ou cadastre uma nova do zero. Depois de vinculada, ela vira um card aqui na lista.",
        },
        {
          anchor: "clinics-card",
          title: "Cada clínica é um card",
          description:
            "Cada clínica em que você atende aparece como um card. No topo ficam o nome e o menu de ações; abaixo, os dados. A clínica ativa/selecionada fica destacada.",
        },
        {
          anchor: "clinics-info",
          title: "Dados da clínica",
          description:
            "Aqui ficam as informações da clínica: CNPJ, endereço completo (rua, bairro, cidade/UF e CEP) e os telefones de contato — com destaque para o telefone principal.",
        },
        {
          anchor: "clinics-google",
          title: firstLinked
            ? "Conta Google da clínica"
            : "Vincule a Conta Google",
          description: googleDescription,
        },
        {
          anchor: "clinics-menu",
          title: "Menu de ações (3 pontinhos)",
          description:
            "Este menu concentra tudo sobre a clínica. Vou detalhar cada opção nos próximos passos: editar, sair, ver a equipe e configurar a agenda.",
        },
        {
          anchor: "clinics-menu",
          title: "Editar / Sair da clínica",
          description:
            "“Editar” abre o cadastro da clínica para ajustar dados (nome, endereço, contatos). “Sair da Clínica” remove o seu vínculo com ela — sem apagar a clínica em si.",
        },
        {
          anchor: "clinics-menu",
          title: "Ver a equipe",
          description:
            "As opções “Profissionais”, “Assistentes” e “Pacientes” abrem janelas para consultar e gerenciar quem está vinculado à clínica em cada papel.",
        },
        {
          anchor: "clinics-menu",
          title: "Configurar Agenda",
          description:
            "A opção “Configurar Agenda” (só para médico) abre a janela onde você define os dias e horários de atendimento e os bloqueios de período daquela clínica.",
        },
      ];
      return steps;
    },

    initializePage() {
      this.filteredClinics = [...this.clinics];
      this.fetchMyClinicsData();
    },

    loadClinics() {
      this.fetchMyClinicsData();
    },

    filterClinics() {
      // salvia-811: o segundo ponto onde a tela estourava com null. A busca roda
      // por watcher, entao dispara antes de a lista chegar.
      const lista = this.clinics || [];

      if (!this.searchQuery) {
        this.filteredClinics = [...lista];
        return;
      }

      const query = this.searchQuery.toLowerCase();
      this.filteredClinics = lista.filter(
        // Campos opcionais no cadastro (cidade, bairro e CNPJ podem vir nulos):
        // buscar nao pode quebrar por causa de clinica incompleta.
        (clinic) =>
          (clinic.name || "").toLowerCase().includes(query) ||
          (clinic.city || "").toLowerCase().includes(query) ||
          (clinic.neighborhood || "").toLowerCase().includes(query) ||
          (clinic.cnpj || "").includes(query),
      );
    },

    formatCNPJ(cnpj) {
      if (!cnpj) return "";
      const cleaned = cnpj.replace(/\D/g, "");
      return cleaned.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        "$1.$2.$3/$4-$5",
      );
    },

    formatCEP(cep) {
      if (!cep) return "";
      const cleaned = cep.replace(/\D/g, "");
      return cleaned.replace(/^(\d{5})(\d{3})$/, "$1-$2");
    },

    formatPhone(phone) {
      if (!phone) return "";
      const cleaned = phone.replace(/\D/g, "");

      if (cleaned.length === 11) {
        return cleaned.replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
      } else if (cleaned.length === 10) {
        return cleaned.replace(/^(\d{2})(\d{4})(\d{4})$/, "($1) $2-$3");
      }
      return phone;
    },

    getPhoneMask(phone) {
      if (!phone) return "";
      const cleaned = phone.replace(/\D/g, "");

      if (cleaned.length === 11) {
        return "(##) #####-####";
      } else if (cleaned.length === 10) {
        return "(##) ####-####";
      }
      return "";
    },

    getPhoneIcon(type) {
      const iconMap = {
        mobile: "mdi-cellphone",
        landline: "mdi-phone",
        commercial: "mdi-phone-classic",
        whatsapp: "mdi-whatsapp",
      };
      return iconMap[type?.toLowerCase()] || "mdi-phone";
    },

    openAddClinicDialog() {
      this.clinicDialogMode = "create";
      this.clinicDialogClinicId = null;
      this.clinicDialog = true;
    },

    createNewClinic() {
      this.clinicDialogMode = "create";
      this.clinicDialogClinicId = null;
      this.clinicDialog = true;
      this.addClinicDialog = false;
    },

    editClinic(clinicId) {
      this.clinicDialogMode = "edit";
      this.clinicDialogClinicId = clinicId;
      this.clinicDialog = true;
    },

    joinExistingClinic() {
      this.addClinicDialog = false;
    },

    async leaveClinic(clinic) {
      if (!clinic?.id || this.leavingClinicId) return;

      const ok = await this.$confirm({
        type: "delete",
        title: "Sair da clínica",
        message: `Tem certeza que deseja sair da clínica <strong>${clinic.name}</strong>?`,
        hint:
          "Você perderá o acesso à agenda, aos pacientes e aos assistentes desta clínica. " +
          "Um novo convite pode restaurar o acesso depois.",
        confirmText: "Sair da clínica",
      });
      if (!ok) return;

      this.leavingClinicId = clinic.id;
      try {
        const message = await leaveClinic(clinic.id);
        this.success(message);
        await this.fetchMyClinicsData();
      } catch (err) {
        console.error("Erro ao sair da clínica:", err);
        // 422 = regra de negócio (ex.: último médico da clínica); a mensagem
        // do backend explica o motivo e vale mais que um texto genérico.
        this.erroDeRequisicao(err, 
          err.response?.data?.message ||
            "Não foi possível sair da clínica. Tente novamente.",
        );
      } finally {
        this.leavingClinicId = null;
      }
    },

    viewPatients(clinicId) {
      this.selectedClinicId = clinicId;
      this.patientsDialog = true;
    },

    viewAssistants(clinicId) {
      this.selectedClinicId = clinicId;
      this.assistantsDialog = true;
    },

    viewDoctors(clinicId) {
      this.selectedClinicId = clinicId;
      this.doctorsDialog = true;
    },

    openAgendaConfig(clinic) {
      this.agendaClinicId = clinic.id;
      this.agendaClinicName = clinic.name;
      this.agendaDialog = true;
    },

    async fetchMyClinicsData() {
      this.loading = true;
      try {
        // salvia-811: cinto e suspensorio. O service ja garante array, mas esta
        // tela quebrava em DOIS pontos com null (aqui e no filterClinics), e
        // "usuario ainda sem clinica" e o estado inicial de toda conta nova.
        this.clinics = (await getMyClinics()) || [];
        this.filteredClinics = [...this.clinics];

        const authPromises = this.clinics.map((clinic) =>
          this.checkGoogleAuth(clinic.id),
        );

        await Promise.all(authPromises);
      } catch (error) {
        console.error("Erro ao buscar clínicas:", error);
        this.$toast?.error?.("Erro ao carregar clínicas");
      } finally {
        this.loading = false;
      }
    },

    async checkGoogleAuth(clinicId) {
      try {
        // Bearer já vai automaticamente pelo interceptor do http
        const { data } = await http.get("/api/check-google-authentication", {
          params: {
            owner_type: "clinic",
            owner_id: clinicId,
          },
        });

        this.$set(this.googleAuthStatus, clinicId, data.authenticated);
      } catch (error) {
        console.error("Erro ao verificar autenticação Google:", error);
        this.$set(this.googleAuthStatus, clinicId, false);
      }
    },

    async redirectToGoogle(clinicId) {
      try {
        // Bearer já vai automaticamente pelo interceptor do http
        const { data } = await http.get("/api/auth/google/redirect", {
          params: {
            owner_type: "clinic",
            owner_id: clinicId,
          },
        });

        if (data?.redirect_url) {
          window.location.assign(data.redirect_url);
        } else {
          throw new Error("URL de redirecionamento não recebida do servidor");
        }
      } catch (error) {
        console.error("Erro durante autenticação com Google:", error);
      }
    },
  },
};
</script>

<style scoped>
.my-clinics-page {
  min-height: 100vh;
}

/* ═══ Page header ═══ */
.page-section-header {
  display: flex;
  align-items: center;
}

.page-section-title {
  font-size: 11px;
  font-weight: 700;
  color: var(--ds-primary);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.page-title {
  font-size: 26px;
  font-weight: 700;
  color: var(--ds-text-strong);
  letter-spacing: -0.4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
}

.page-subtitle {
  font-size: 13px;
  color: var(--ds-text-soft);
  font-weight: 500;
  margin: 0;
}

/* ═══ Loading / empty state ═══ */
.loading-text {
  font-size: 13px;
  color: var(--ds-text-soft);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-weight: 600;
}

.empty-state-card {
  border-radius: 16px !important;
  border: 1.5px dashed var(--ds-blue-200) !important;
  background: var(--ds-blue-50) !important;
  box-shadow: none !important;
}

.empty-state-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--ds-primary);
  letter-spacing: -0.2px;
}

.empty-state-subtitle {
  font-size: 13px;
  color: var(--ds-text-soft);
  font-weight: 500;
  line-height: 1.6;
  max-width: 480px;
  margin: 0 auto;
}

.empty-state-btn {
  font-size: 13px !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  text-transform: none !important;
  box-shadow: 0 4px 12px rgba(31, 77, 63, 0.25) !important;
}

/* ═══ Card de clínica ═══ */
.clinic-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease,
    border-color 0.3s ease;
  border-radius: 14px !important;
  overflow: hidden;
  border: 1px solid var(--ds-blue-100) !important;
  box-shadow: 0 2px 10px rgba(31, 77, 63, 0.06) !important;
}

.clinic-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 28px rgba(31, 77, 63, 0.16) !important;
  border-color: var(--ds-blue-200) !important;
}

.clinic-header {
  background: var(--ds-gradient-header);
  position: relative;
  color: white !important;
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  min-width: 0;
  padding: 10px 16px !important;
}

.clinic-title-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
  color: white !important;
  letter-spacing: -0.2px;
}

.clinic-header::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.06) 1px, transparent 1px);
  background-size: 28px 28px;
  opacity: 0.22;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.clinic-card:hover .clinic-header::before {
  opacity: 0.45;
}

/* Labels de seção do card */
.clinic-card :deep(.text-subtitle-2) {
  font-size: 10px !important;
  font-weight: 700 !important;
  color: var(--ds-primary) !important;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.clinic-card :deep(.v-card__text) {
  font-size: 13px;
  color: var(--ds-text-600);
  background-color: var(--ds-blue-50) !important; /* corpo do card na cor do profile-hero */
}

.clinic-card :deep(.v-card__actions) {
  background-color: var(--ds-blue-50) !important; /* rodapé do card na mesma cor do corpo */
}

/* Inputs e selects na cor do profile-hero */
.my-clinics-page :deep(.v-text-field .v-input__slot),
.my-clinics-page :deep(.v-select .v-input__slot),
.my-clinics-page :deep(.v-autocomplete .v-input__slot) {
  background-color: var(--ds-blue-50) !important;
}

.gap-1 {
  gap: 4px;
}

/* ═══ Interior do card: label à esquerda, valor à direita (ocupa a largura toda) ═══ */
.clinic-card .info-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 11px 2px;
}

.clinic-card .info-key {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding-top: 1px;
}

.clinic-card .info-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--ds-primary);
}

.clinic-card .info-val {
  flex: 1 1 auto;
  min-width: 0;
  text-align: right;
  font-size: 13px;
  font-weight: 600;
  color: var(--ds-text-700);
  line-height: 1.5;
  word-break: break-word;
}

.clinic-card .info-val-sub {
  font-weight: 400;
  font-size: 12.5px;
  color: var(--ds-text-500);
}

.clinic-card .info-phone-line {
  flex-wrap: wrap;
  row-gap: 4px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
}

.clinic-card .info-phone-line + .info-phone-line {
  margin-top: 5px;
}

.clinic-card .info-divider {
  border-color: #eef2f6 !important;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.full-width {
  width: 100%;
}

/* Botão Google na ação do card */
.clinic-card :deep(.v-card__actions .v-btn) {
  text-transform: none !important;
  letter-spacing: 0.2px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
}

/* Responsividade para o botão */
@media (max-width: 960px) {
  .full-width {
    margin-top: 8px;
  }
}
</style>
